/**
 * FeedPage.tsx — 2004va
 *
 * Two modes depending on role:
 *
 * CREATOR — their own shots, newest first.
 *   - Grade badge always visible
 *   - Held shots show lock + "Release to market" CTA
 *   - Routed shots show routing indicator
 *   - Challenge pending badge
 *   - Tap card → detail sheet (editor note, scores)
 *
 * BUYER / PRESS / ORGANISER — marketplace feed.
 *   - Only processed/routed shots with score >= 0.6
 *   - Sorted by AI score descending — best first
 *   - Tap card → request license sheet
 *   - Grade + score always visible
 *   - Creator name + event shown
 *
 * Realtime: new uploads and status updates appear live.
 */

import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Zap, Send, ChevronRight, X, Check, Film, Music, FileText } from 'lucide-react'
import {
  fetchCreatorFeed, fetchMarketplaceFeed,
  releaseMediaToMarket, triggerPushEngine, supabase,
} from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/ui'
import { PublishSheet } from '@/components/publish/PublishSheet'
import { cn } from '@/components/ui/utils'
import type { MediaItem } from '@/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

const GRADE_STYLE: Record<string, string> = {
  A: 'bg-emerald-900/70 text-emerald-300 border-emerald-700/40',
  B: 'bg-blue-900/70 text-blue-300 border-blue-700/40',
  C: 'bg-amber-900/70 text-amber-300 border-amber-700/40',
  D: 'bg-red-900/70 text-red-300 border-red-700/40',
}

function thumbUrl(url: string) {
  return url.replace('/upload/', '/upload/w_600,q_75,f_auto/')
}

// ── Detail sheet — shown on card tap ─────────────────────────────────────────

function DetailSheet({
  item, isOwner, onClose, onReleased,
}: {
  item:       MediaItem
  isOwner:    boolean
  onClose:    () => void
  onReleased: (id: string) => void
}) {
  const [releasing, setReleasing] = useState(false)
  const [released,  setReleased]  = useState(false)
  const [showPublish, setShowPublish] = useState(false)

  async function handleRelease() {
    setReleasing(true)
    try {
      await releaseMediaToMarket(item.id)
      await triggerPushEngine(item.id)
      setReleased(true)
      onReleased(item.id)
    } catch (e) {
      console.error('Release error:', e)
    } finally {
      setReleasing(false)
    }
  }

  const isHeld      = item.status === 'held'
  const grade       = (item as MediaItem & { ai_grade?: string }).ai_grade
  const editorNote  = (item as MediaItem & { ai_editor_note?: string }).ai_editor_note
  const techScores  = (item as MediaItem & { ai_technical_scores?: Record<string,number> }).ai_technical_scores
  const editScores  = (item as MediaItem & { ai_editorial_scores?: Record<string,number> }).ai_editorial_scores
  const commScores  = (item as MediaItem & { ai_commercial_scores?: Record<string,number> }).ai_commercial_scores

  return (
    <>
      <div className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-40 max-w-lg mx-auto px-4 pb-4">
        <div className="bg-room-bg border border-room-border rounded-2xl overflow-hidden shadow-2xl">

          {/* Media preview */}
          <div className="relative aspect-video w-full overflow-hidden bg-room-surface">
            {item.media_type === 'photo' ? (
              <img src={thumbUrl(item.cloudinary_url)} alt="" className="w-full h-full object-cover" />
            ) : item.media_type === 'video' ? (
              <video
                src={item.cloudinary_url}
                controls
                className="w-full h-full object-contain bg-black"
                playsInline
              />
            ) : item.media_type === 'audio' ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-room-surface/50 px-6">
                <Music size={28} className="text-room-accent" />
                <audio src={item.cloudinary_url} controls className="w-full" />
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-room-surface/50">
                <FileText size={28} className="text-room-accent" />
                <a href={item.cloudinary_url} target="_blank" rel="noreferrer"
                  className="text-xs text-room-accent underline underline-offset-2">
                  Open document
                </a>
              </div>
            )}
            <button type="button" onClick={onClose}
              className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white">
              <X size={13} />
            </button>
            {grade && (
              <span className={cn(
                'absolute top-3 left-3 text-xs font-black px-2 py-0.5 rounded-lg border',
                GRADE_STYLE[grade] ?? GRADE_STYLE.D
              )}>
                Grade {grade}
              </span>
            )}
          </div>

          <div className="p-4 space-y-4">
            {/* Meta */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold truncate">
                  {item.participant?.name ?? 'Anonymous'}
                </p>
                <p className="text-room-muted text-xs mt-0.5">
                  {item.event?.name ?? 'No event'}{item.event?.venue ? ` · ${item.event.venue}` : ''}
                </p>
              </div>
              {item.ai_route_score != null && (
                <span className="flex items-center gap-1 text-room-gold text-sm font-bold flex-shrink-0">
                  <Zap size={12} />
                  {Math.round(item.ai_route_score * 100)}%
                </span>
              )}
            </div>

            {/* Editor note */}
            {editorNote && (
              <p className="text-xs text-neutral-300 leading-relaxed italic border-l-2 border-room-accent/40 pl-3">
                {editorNote}
              </p>
            )}

            {/* Score breakdown — collapsed by default */}
            {(techScores || editScores || commScores) && (
              <ScoreBreakdown tech={techScores} editorial={editScores} commercial={commScores} />
            )}

            {/* Actions */}
            {isOwner && isHeld && !released && (
              <button
                type="button"
                onClick={handleRelease}
                disabled={releasing}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-3 rounded-xl',
                  'bg-room-accent text-white font-semibold text-sm',
                  'hover:bg-room-accent/90 transition-all shadow-lg shadow-room-accent/20',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {releasing ? <Spinner size={15} /> : <Send size={15} />}
                {releasing ? 'Releasing…' : 'Release to market'}
              </button>
            )}

            {released && (
              <div className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-900/30 border border-emerald-700/30">
                <Check size={15} className="text-emerald-400" />
                <span className="text-emerald-300 text-sm font-semibold">Released — routing now</span>
              </div>
            )}

            {/* Publish to own channels — creator only */}
            {isOwner && !isHeld && (
              <button
                type="button"
                onClick={() => setShowPublish(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-room-border text-xs font-semibold text-room-muted hover:text-white hover:border-white/20 transition-all"
              >
                Publish to your channels
              </button>
            )}

            {showPublish && (
              <PublishSheet
                mediaItemId={item.id}
                cloudinaryUrl={item.cloudinary_url}
                mediaType={item.media_type ?? 'photo'}
                fileName={item.file_name ?? 'media'}
                grade={(item as MediaItem & { ai_grade?: string }).ai_grade}
                description={(item as MediaItem & { ai_description?: string }).ai_description ?? ''}
                mood={item.ai_mood ?? undefined}
                tags={(item as MediaItem & { ai_tags?: string[] }).ai_tags ?? []}
                eventName={item.event?.name}
                onClose={() => setShowPublish(false)}
              />
            )}

            {!isOwner && (
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-room-muted">Interested in this shot?</span>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex items-center gap-1.5 text-xs font-semibold text-room-accent"
                >
                  Go to Inbox <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── Score breakdown ───────────────────────────────────────────────────────────

function ScoreBreakdown({
  tech, editorial, commercial,
}: {
  tech?:       Record<string,number>
  editorial?:  Record<string,number>
  commercial?: Record<string,number>
}) {
  const [open, setOpen] = useState(false)

  if (!tech && !editorial && !commercial) return null

  return (
    <div>
      <button type="button" onClick={() => setOpen(v => !v)}
        className="text-xs text-room-muted hover:text-white transition-colors flex items-center gap-1">
        {open ? 'Hide' : 'Show'} score breakdown
        <ChevronRight size={10} className={cn('transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[
            { label: 'Technical',   scores: tech },
            { label: 'Editorial',   scores: editorial },
            { label: 'Commercial',  scores: commercial },
          ].map(({ label, scores }) => scores && (
            <div key={label} className="bg-room-surface rounded-xl p-2 space-y-1">
              <p className="text-[10px] font-semibold text-room-muted uppercase tracking-widest">{label}</p>
              {Object.entries(scores).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <div className="flex-1 bg-room-border rounded-full h-1 overflow-hidden">
                    <div className="h-full bg-room-accent/60 rounded-full" style={{ width: `${(v / 10) * 100}%` }} />
                  </div>
                  <span className="text-[9px] text-room-muted w-3 text-right">{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Feed card ─────────────────────────────────────────────────────────────────

function FeedCard({
  item, isOwner, onTap,
}: {
  item:    MediaItem
  isOwner: boolean
  onTap:   (item: MediaItem) => void
}) {
  const grade       = (item as MediaItem & { ai_grade?: string }).ai_grade
  const isHeld      = item.status === 'held'
  const isChallenge = (item as MediaItem & { challenge_status?: string }).challenge_status === 'pending'
  const isRouted    = item.status === 'routed'

  const typeIcon = item.media_type === 'video'
    ? <Film size={22} className="text-room-muted" />
    : item.media_type === 'audio'
    ? <Music size={22} className="text-room-muted" />
    : <FileText size={22} className="text-room-muted" />

  return (
    <button
      type="button"
      onClick={() => onTap(item)}
      className="group relative aspect-square rounded-xl overflow-hidden bg-room-surface border border-room-border hover:border-room-accent/50 transition-all duration-200 text-left"
    >
      {/* Media preview */}
      {item.media_type === 'photo' ? (
        <img
          src={thumbUrl(item.cloudinary_url)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : item.media_type === 'video' ? (
        <div className="w-full h-full relative bg-black">
          <video
            src={item.cloudinary_url}
            className="w-full h-full object-cover"
            muted playsInline preload="metadata"
            onMouseEnter={e => (e.target as HTMLVideoElement).play()}
            onMouseLeave={e => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0 }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
              <Film size={14} className="text-white" />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-room-surface/80">
          {typeIcon}
          <span className="text-room-muted text-[10px] font-semibold uppercase tracking-widest">
            {item.media_type}
          </span>
          {item.file_name && (
            <span className="text-room-muted/60 text-[9px] px-2 text-center truncate w-full">
              {item.file_name}
            </span>
          )}
        </div>
      )}

      {/* Held overlay */}
      {isOwner && isHeld && (
        <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-1.5">
          <Lock size={16} className="text-amber-300" />
          <span className="text-[10px] text-amber-300 font-semibold">Held</span>
          <span className="text-[9px] text-amber-300/60">Tap to release</span>
        </div>
      )}

      {/* Grade badge */}
      {grade && (
        <span className={cn(
          'absolute top-1.5 left-1.5 text-[10px] font-black px-1.5 py-0.5 rounded-lg border',
          GRADE_STYLE[grade] ?? GRADE_STYLE.D
        )}>
          {grade}
        </span>
      )}

      {/* Routed indicator */}
      {isRouted && isOwner && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" title="Routed to buyers" />
      )}

      {/* Challenge badge */}
      {isChallenge && (
        <span className="absolute bottom-1.5 right-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-purple-900/80 text-purple-300 border border-purple-700/40">
          Review
        </span>
      )}

      {/* Hover overlay — score + mood */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-2 flex flex-col justify-end gap-0.5">
        {item.ai_route_score != null && (
          <span className="flex items-center gap-1 text-room-gold text-[10px] font-semibold">
            <Zap size={9} />
            {Math.round(item.ai_route_score * 100)}%
          </span>
        )}
        {item.ai_mood && (
          <span className="text-room-accent text-[10px] capitalize">{item.ai_mood}</span>
        )}
        {!isOwner && item.participant?.name && (
          <span className="text-white/70 text-[10px] truncate">{item.participant.name}</span>
        )}
      </div>
    </button>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function FeedPage() {
  const { participant }   = useAuthStore()
  const queryClient       = useQueryClient()
  const isCreator         = participant?.role === 'creator' || !participant?.role
  const [selected, setSelected] = useState<MediaItem | null>(null)
  const [_releasedIds, setReleasedIds] = useState<Set<string>>(new Set())

  // Choose feed based on role
  const { data: items = [], isLoading } = useQuery<MediaItem[]>({
    queryKey:  isCreator ? ['creator-feed', participant?.id] : ['marketplace-feed'],
    queryFn:   isCreator
      ? () => fetchCreatorFeed(participant!.id)
      : () => fetchMarketplaceFeed(),
    enabled:   !!participant,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // Realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('feed-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'media_items' }, () => {
        queryClient.invalidateQueries({ queryKey: ['creator-feed'] })
        queryClient.invalidateQueries({ queryKey: ['marketplace-feed'] })
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'media_items' }, () => {
        queryClient.invalidateQueries({ queryKey: ['creator-feed'] })
        queryClient.invalidateQueries({ queryKey: ['marketplace-feed'] })
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [queryClient])

  function handleReleased(id: string) {
    setReleasedIds(prev => new Set([...prev, id]))
    queryClient.invalidateQueries({ queryKey: ['creator-feed'] })
    setSelected(null)
  }

  if (isLoading) return (
    <div className="grid grid-cols-3 gap-2">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="aspect-square rounded-xl bg-room-surface animate-pulse" />
      ))}
    </div>
  )

  const heldCount    = items.filter(i => i.status === 'held').length
  const routedCount  = items.filter(i => i.status === 'routed').length

  return (
    <div className="space-y-4 animate-fade-in">

      {/* ── Header bar ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-white">
            {isCreator ? 'Your shots' : 'Marketplace'}
          </p>
          <span className="text-xs text-room-muted">{items.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {isCreator && heldCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-amber-300 font-semibold">
              <Lock size={10} />
              {heldCount} held
            </span>
          )}
          {isCreator && routedCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-emerald-400 font-semibold">
              <Send size={10} />
              {routedCount} live
            </span>
          )}
        </div>
      </div>

      {/* ── Held banner — creator with held shots ── */}
      {isCreator && heldCount > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <Lock size={14} className="text-amber-300 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-300">
              {heldCount} shot{heldCount > 1 ? 's' : ''} held — not yet in the marketplace
            </p>
            <p className="text-[11px] text-amber-300/60 mt-0.5">
              Tap any held shot to review the AI verdict and release individually, or add your contact to release all at once.
            </p>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {items.length === 0 && (
        <div className="text-center py-16 space-y-2">
          <p className="text-room-muted text-sm">
            {isCreator ? 'No shots yet — start a shoot to see your work here' : 'No shots available yet'}
          </p>
        </div>
      )}

      {/* ── Grid ── */}
      <div className="grid grid-cols-3 gap-2">
        {items.map(item => (
          <FeedCard
            key={item.id}
            item={item}
            isOwner={isCreator && item.participant_id === participant?.id}
            onTap={setSelected}
          />
        ))}
      </div>

      {/* ── Detail sheet ── */}
      {selected && (
        <DetailSheet
          item={selected}
          isOwner={isCreator && selected.participant_id === participant?.id}
          onClose={() => setSelected(null)}
          onReleased={handleReleased}
        />
      )}
    </div>
  )
}
